<template>
  <el-dialog
    :title="generateTitle"
    :visible.sync="visible"
    size="large"
    :show-close="false"
    :close-on-press-escape="false"
    :close-on-click-modal="false">
    <el-row type="flex" align="middle" justify="start" style="margin-bottom: 12px;">
      <div style="display: inline-flex; width: 10px; height: 10px;" class="cell-new"> </div>
      <div style="display: inline-flex; margin-left: 6px">New value</div>
      <div style="display: inline-flex; width: 10px; height: 10px; margin-left: 24px;" class="cell-original"> </div>
      <div style="display: inline-flex; margin-left: 6px">Previous value</div>
    </el-row>
    <el-row v-if="allDcs.length">
      <el-col>
        <el-row type="flex" align="middle" justify="center">
          <div style="width: 100%;">
            <table class="dc-table" style="width: 100%;">
              <colgroup>
                <col v-for="field in lineFieldsFiltered" v-if="!['company', 'production'].includes(field.name) && !field.hidden" :style="setStyle(field.name)">
              </colgroup>
              <tr>
                <th v-for="field in lineFieldsFiltered" v-if="!['company', 'production'].includes(field.name) && !field.hidden">
                  {{ field.label }}
                </th>
              </tr>
              <tr
                v-for="dc in allDcsWithDiff"
                :style="dc.original ? 'border-bottom: 6px double #dfe6ec;' : null"
                class="dc-row">
                <td v-if="dc.original">{{ `${dc.line_number} (original)` }}</td>
                <td v-else-if="dc.splits">{{ `${dc.line_number} (splits #${dc.splits})`}}</td>
                <td v-else>{{ dc.line_number }}</td>
                <td
                  v-for="field in lineFieldsFiltered"
                  v-if="!['line_number', 'company', 'production'].includes(field.name) && !field.hidden"
                  :class="getCellClass(dc, field.name)">
                  <div v-if="field.name === 'asset'" style="text-align: center;">
                    <el-checkbox :value="getFormattedFieldValue(dc.form[field.name])" disabled />
                  </div>
                  <div v-else :style="`text-align: ${field.name === 'amount_decimal' ? 'right' : 'left'}`">
                    {{ getFormattedFieldValue(dc.form[field.name]) }}
                  </div>
                </td>
              </tr>
            </table>
          </div>
        </el-row>
      </el-col>
    </el-row>
    <el-row type="flex" justify="end" align="middle" style="padding: 20px 0 0 0;">
      <el-button @click="$emit('cancel')" :disabled="loading">
        Cancel
      </el-button>
      <el-button type="primary" @click="$emit('confirm')" :loading="loading">
        Confirm
      </el-button>
    </el-row>
  </el-dialog>
</template>

<script>
  import { capitalizeFirstLetter } from '@/utils/helpers/StringOperations'

  export default {
    props: {
      originalLines: {
        default: () => [],
        type: Array
      },
      lineDeltas: {
        default: () => {},
        type: Object
      },
      visible: {
        default: false,
        type: Boolean
      },
      lineFields: {
        default: () => [],
        type: Array
      },
      allDcs: {
        default: () => [],
        type: Array
      },
      newDcs: {
        default: () => [],
        type: Array
      },
      jesToVoid: {
        default: () => {},
        type: Object
      },
      loading: {
        default: () => false,
        type: Boolean
      }
    },
    data () {
      return {
        viewingDiff: null
      }
    },
    methods: {
      /**
       * Returns the class name that should be assigned to the <td> element for the field name provided in
       * the fieldName argument. Possible class names are 'cell-original' or 'cell-new', which
       * is determined by checking whether the field's value was changed in a DC.
       * @param dc
       * @param fieldName
       */
      getCellClass (dc, fieldName) {
        if (!(this.lineDeltas[dc.form.id] && this.lineDeltas[dc.form.id].hasOwnProperty(fieldName))) {
          return dc.splits ? 'cell-new' : null // field was not changed
        }
        return dc.original ? 'cell-original' : 'cell-new'
      },
      /**
       * Because typeahead autocomplete fields must have 'label' and 'value' properties, extracts the actual
       * label by checking whether it has a 'label' property.
       */
      getFormattedFieldValue (field) {
        if (field && field.hasOwnProperty('label')) {
          return field.label
        }
        return field
      },
      /**
       * Returns field-specific styles.
       * @param field   The field name
       */
      setStyle (field) {
        let style = { 'text-align': 'left' }
        switch (field) {
          case 'line_number':
            style['width'] = '3%'
            style['text-align'] = 'center'
            break
          case 'relieves':
            style['width'] = '10%'
            break
          case 'amount_decimal':
            style['width'] = '8%'
            break
          case 'account':
          case 'location':
          case 'series':
          case 'set':
          case 'tax_code':
          case 'free_field_1':
          case 'free_field_2':
          case 'free_field_3':
          case 'free_field_4':
            style['width'] = '4%'
            break
          case 'asset':
            style['width'] = '3%'
            style['text-align'] = 'center'
            break
          case 'third_party_vendor':
          case 'other_name':
            style['width'] = '6%'
            break
        }
        return style
      },
      /**
       * Sets the label and value properties on typeahead/autocomplete fields. This is necessary because these types
       * of fields must display a value that is different than their actual value (i.e. display a code, but the
       * actual value is an id).
       * @param form
       */
      setFormLabelsAndValues (form) {
        const { getters } = this.$store
        const ret = {}
        for (let field in form) {
          const getter = () => getters[`get${capitalizeFirstLetter(field.replace(/[^A-Za-z]+/g, ''))}ById`]
          const id = form[field]
          switch (field) {
            case 'relieves':
              ret[field] = Object.values(getters.getPoLineById(id)) ? getters.getPoLineById(id).meta_name : ''
              break
            case 'account':
            case 'series':
            case 'location':
            case 'set':
            case 'tax_code':
            case 'free_field_1':
            case 'free_field_2':
            case 'free_field_3':
            case 'free_field_4':
              ret[field] = Object.values(getter()(id)) && getter()(id).code ? getter()(id).code : ''
              break
            case 'third_party_vendor':
              ret[field] = getters.getVendorById(id).code
              break
            default:
              ret[field] = id
          }
        }
        return ret
      }
    },
    computed: {
      /**
       * Returns the lineFields prop value without display-only fields such as 'account:name'
       */
      lineFieldsFiltered () {
        return this.lineFields.filter(field => field.name !== 'account:name')
      },
      /**
       * Returns the modal title string.
       */
      generateTitle () {
        if (this.allDcs && this.allDcs.length) {
          return `Confirm ${this.allDcs.length} Distribution Change${this.allDcs.length > 1 ? 's' : ''}`
        }
      },
      /**
       * Returns an array of all DCs, sorted by line_number value.
       */
      allDcsSorted () {
        return this.allDcs.sort((a, b) => a.line_number - b.line_number)
      },
      /**
       * TODO: deprecate
       */
      newDcsSorted () {
        return this.newDcs.sort((a, b) => a.line_number - b.line_number)
      },
      /**
       * Returns all DCs with original lines inserted
       * @return {[]}
       */
      allDcsWithDiff () {
        let linesWithDiffRows = []
        const getOriginalLine = (id) => this.originalLines.find(line => line.form.id === id)
        const lineFields = this.lineFieldsFiltered.map(field => field.name)
        this.allDcsSorted.forEach(dcLine => {
          linesWithDiffRows.push(dcLine)
          let original = dcLine.form.id ? getOriginalLine(dcLine.form.id) : null
          if (original) {
            const originalFormCopy = { ...original.form } // Since objects are pass-by-reference, clone the original line's form to avoid undesireable side effects
            for (let field in originalFormCopy) {
              if (field !== 'id' && !lineFields.includes(field)) {
                delete originalFormCopy[field]
              }
            }
            const formattedOriginal = {
              form: this.setFormLabelsAndValues(originalFormCopy),
              line_number: original.line_number,
              original: true
            }
            linesWithDiffRows.push(formattedOriginal)
          }
        })
        return linesWithDiffRows
      }
    }
  }
</script>

<style scoped>
  table, th, td, tr {
    border-collapse: collapse;
    border: 1px solid #dfe6ec;
  }
  th {
    padding: 6px;
    background-color: #eef1f6;
  }
  td {
    max-width: 100px;
    padding: 6px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .dc-row {
    background: #ffffff;
  }
  .cell-original {
    background: #fcbdbd;
  }
  .cell-new {
    background: #bfffbf;
  }
</style>



// WEBPACK FOOTER //
// src/components/DcConfirmationDialog.vue